export const VDA_NO_HUDDLE_ONBOARDING_CONTENT = {
  slug: 'setup-vca',
  logo: 'VideaAI',
  title: 'VideaAI Setup',
  description:
    'This 5-minute set-up process is required for all users.\n We will introduce some new expectations for AI findings and record your preferences for using each finding type.',
  closeUrl: 'huddle',
  slides: [
    {
      id: 0,
      title: 'Welcome to VideaAI',
    },
    {
      id: 100,
      title: 'What to expect from Dental Image AI',
      logo: 'VideaAI',
      template: 'intro',
      isEmbed: false,
      sections: [
        {
          title: 'AI findings from radiographs',
          color: 'success',
          images: [],
          paragraphs: [
            {
              text: 'VideaAI, equipped with the collective expertise of hundreds of dentists, is a cleared medical device, giving you peace of mind regarding its safety for patient use. AI findings are automatically marked on your x-rays, serving as a helpful guide in your diagnostic process. Think of VideaAI as your trusty assistant, directing your attention to specific areas for evaluation and granting you (providers) complete diagnostic autonomy in making the final treatment decision.',
              color: 'default',
              classLabel: 'content-large',
            },
          ],
        },
      ],
    },
    {
      id: 200,
      title: 'The VideaAI image viewer and controls',
      logo: 'VideaAI',
      template: 'intro',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Viewer Interface',
          color: 'success',
          images: [
            {
              alt: 'Diagram of viewer interface',
              imageUri: '/assets/images/onboarding/viewer-01.png',
              scale: '2.5x',
              format: 'inline-block',
              caption:
                'Switch between boxes and shapes for annotations with the Patient View toggle.',
            },
            {
              alt: 'Diagram of toggle to hide or show annotations',
              imageUri: '/assets/images/onboarding/viewer-02.png',
              scale: '2x',
              format: 'inline-block',
              caption:
                'To show or hide annotations, use the toggles (for restorative, perio, or endo findings).',
            },
            {
              alt: 'Diagram of toggle to hide or show annotations',
              imageUri: '/assets/images/onboarding/viewer-03.png',
              scale: '2.5x',
              format: 'inline-block',
              captionTitle: 'Keyboard Shortcuts',
              caption:
                'Most actions can be done with the keyboard. A keyboard icon displays all shortcuts.',
            },
          ],
          paragraphs: [],
        },
        {
          order: 2,
          title: 'Viewing historical radiographs',
          color: 'success',
          paragraphs: [],
          images: [
            {
              alt: 'Diagram of image comparison',
              imageUri: '/assets/images/onboarding/viewer-04.png',
              scale: '2x',
              format: 'inline-block',
              caption:
                'To compare radiographs across visits, select the “compare visits” button (when available).',
            },
            {
              alt: 'Diagram of image controls',
              imageUri: '/assets/images/onboarding/viewer-05.png',
              scale: '2x',
              format: 'inline-block',
              caption:
                'To adjust the brightness and contrast for radiographs, use the image tools.',
            },
          ],
        },
      ],
    },
    {
      id: 300,
      title: 'What to expect from AI Caries Detection',
      subtitle: 'Potential carious lesions: annotations',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Display annotation with box',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with caries annotation',
              imageUri: '/assets/images/onboarding/vda-caries-1.png',
              scale: '2.5x',
              format: 'block',
              caption:
                'Identifies potential carious lesions using boxes, used by providers to guide comprehensive intra-oral examination.',
            },
          ],
          paragraphs: [],
        },
        {
          title: 'Display annotation with color shape',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with caries isocontour',
              imageUri: '/assets/images/onboarding/vda-caries-2.png',
              scale: '2.5x',
              format: 'block',
              caption:
                'Draws out potential carious lesions, used by the clinical team to educate patients.',
            },
          ],
          paragraphs: [],
        },
        {
          color: 'success',
          images: [
            {
              alt: 'Diagram of tooth with caries',
              imageUri: '/assets/images/onboarding/vda-caries-3.png',
              scale: '2.5x',
              format: 'block',
              captionTitle: null,
              caption: 'Interproximal, Occlusal, Gross / Root, Buccal/lingual',
            },
          ],
          paragraphs: [],
        },
        {
          images: [],
          paragraphs: [
            {
              text: 'Inter-proximal\n Occlusal \n Gross / Root',
              color: 'default',
              classLabel: 'content-medium',
            },
            {
              title: 'Potential Depth',
              text: 'Into Enamel \n Into Dentin \n Into Pulp',
              color: 'default',
              classLabel: 'content-medium',
            },
          ],
        },
      ],
    },
    {
      id: 320,
      title: 'What to expect from AI Caries Detection',
      subtitle: 'Some factors that can interfere with AI detection',
      color: 'warning',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Grooves or fossae',
          color: 'warning',
          icon: 'warning',
          images: [
            {
              alt: 'Diagram of tooth with caries annotation',
              imageUri: '/assets/images/onboarding/vda-caries-4.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
        {
          title: 'Cervical burnout',
          color: 'warning',
          icon: 'warning',
          images: [
            {
              alt: 'Diagram of tooth with cervical burnout',
              imageUri: '/assets/images/onboarding/vda-caries-5.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
        {
          images: [
            {
              alt: 'Examples of substandard radiographs for AI detection.',
              imageUri: '/assets/images/onboarding/vda-caries-6.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [
            {
              text: 'Other contributing factors for AI detection problems:',
              color: 'default',
              classLabel: 'content-medium',
            },
            {
              text: 'Cone cuts \n Over/under exposure \n Image noise or artifacts \n Inappropriate angulation or pre-sharpening',
              icon: 'warning',
              color: 'warning',
              classLabel: 'content-medium',
            },
          ],
        },
      ],
    },
    {
      id: 340,
      title: 'What to expect from AI Caries Detection',
      subtitle: 'AI detections may occasionally miss potential caries',
      color: 'warning',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Some occlusal caries',
          color: 'warning',
          icon: 'warning',
          images: [
            {
              alt: 'Diagram of tooth with occlusal caries annotation',
              imageUri: '/assets/images/onboarding/vda-caries-7.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
        {
          title: 'Some lingual or buccal caries',
          color: 'warning',
          icon: 'warning',
          images: [
            {
              alt: 'Diagram of tooth with buccal caries',
              imageUri: '/assets/images/onboarding/vda-caries-8.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
        {
          title: 'Some tooth overlap hides decay',
          color: 'warning',
          icon: 'warning',
          images: [
            {
              alt: 'Diagram of tooth with overlap',
              imageUri: '/assets/images/onboarding/vda-caries-9.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
      ],
    },
    {
      id: 360,
      title: 'What to expect from AI Caries Detection',
      subtitle: 'Display Threshold  Preference',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: true,
      sections: [
        {
          title: null,
          subtitle: null,
          color: 'default',
          icon: null,
          embed: 'thresholdSettings',
          paragraphs: [
            {
              title: null,
              text: 'Please select your default threshold for pathology display. (You can always change it again later, for your practice or just for a set of radiographs.)',
              icon: null,
              color: 'default',
              classLabel: 'content-large',
            },
          ],
        },
      ],
    },
    {
      id: 380,
      title: 'What to expect from AI Caries Detection',
      subtitle: 'Detection of potential restorative imperfections',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Display annotation with yellow dotted-line box',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with caries annotation',
              imageUri: '/assets/images/onboarding/vda-ri-1.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
        {
          images: [],
          paragraphs: [
            {
              text: 'Examples of lucencies not showing decay:',
              color: 'default',
              classLabel: 'content-medium',
            },
            {
              text: 'Imperfect/open crown margins \n Filling liners/voids \n Excess or overhanging restorative material',
              color: 'success',
              classLabel: 'content-medium',
            },
          ],
        },
      ],
    },
    {
      id: 400,
      title: 'What to expect from AI Perio Measurement',
      subtitle: 'Radiographic bone level: annotations',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title:
            'On bitewing radiographs, bone level is measured in millimeters',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with rbl annotation',
              imageUri: '/assets/images/onboarding/vda-rbl-1.png',
              scale: '2.25x',
              format: 'block',
            },
          ],
          paragraphs: [],
        },
        {
          title:
            'On periapical radiographs, bone level is measured in % from the root',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of teeth with rbl lines',
              imageUri: '/assets/images/onboarding/rbl-02.png',
              scale: '2.25x',
              format: 'block',
            },
          ],
          paragraphs: [],
        },
        {
          images: [],
          paragraphs: [
            {
              text: 'Radiographic bone loss (RBL) is utilized as an alternative method by the American Academy of Periodontology for staging periodontal disease in cases where clinical attachment loss (CAL) data is unavailable. This approach, in conjunction with the assessment of tooth loss due to periodontitis and other complexity factors, can lead to a modification or elevation of the disease stage.',
              color: 'default',
              classLabel: 'content-bold',
            },
            {
              title:
                'RBL measurements will not necessarily correspond to periodontal chart depth measurements.',
              text: '',
              icon: 'warning',
              color: 'warning',
              classLabel: 'content-medium',
            },
          ],
        },
      ],
    },
    {
      id: 500,
      title: 'What to expect from AI Perio Detection',
      subtitle: 'Choose your RBL practice default colors',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: true,
      sections: [
        {
          title: null,
          subtitle: null,
          color: 'default',
          icon: null,
          images: [],
          paragraphs: [
            {
              title: null,
              text: 'Videa Perio Assist is a medical device focused on measurement of radiographic bone level (RBL). To adjust the thresholds for the colors assigned to the measurements, adjust the slider.',
              icon: null,
              color: 'default',
              classLabel: 'content-large',
            },
          ],
        },
        {
          title: null,
          subtitle: null,
          color: 'default',
          icon: null,
          embed: 'rblSettings',
          paragraphs: [],
        },
        {
          title: null,
          subtitle: null,
          color: 'default',
          icon: null,
          paragraphs: [
            {
              title: null,
              text: 'The American Academy of Periodontology associates the following RBL measurements with these Periodontitis stages:\n\nStage I: Coronal third (<15%)\nStage II: Coronal third (<30%)\n Stage III: Extending to middle third of root and beyond \n Stage IV: Extending to middle third of root and beyond',
              icon: null,
              color: 'default',
              classLabel: 'content-small',
            },
          ],
        },
      ],
    },
    {
      id: 600,
      title: 'What to expect from AI Perio Detection',
      subtitle: 'Detection of potential calculus',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Display annotation with bounding-circle',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with calculus annotation',
              imageUri: '/assets/images/onboarding/vda-calculus-1.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
        {
          images: [],
          paragraphs: [
            {
              text: 'Presence of calculus often indicates potential periodontal disease, combined with perio charting.',
              color: 'default',
              classLabel: 'content-medium',
            },
          ],
        },
      ],
    },
    {
      id: 700,
      title: 'What to expect from AI PRL Detection',
      subtitle: 'Detection of potential periapical radiolucency',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Display annotation with bounding-circle',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with prl annotation',
              imageUri: '/assets/images/onboarding/vda-prl-1.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
        {
          title:
            'AI Detections may incorrectly annotate some anatomy (mental foramen, sinus)',
          color: 'warning',
          icon: 'warning',
          images: [
            {
              alt: 'Diagram of tooth with false positive prl annotation',
              imageUri: '/assets/images/onboarding/vda-prl-2.png',
              scale: '2.5x',
              format: 'block',
              caption: '',
            },
          ],
          paragraphs: [],
        },
      ],
    },
    {
      id: 500,
      title: 'Congratulations!',
    },
  ],
};
